import React from 'react';
import { FaPlus, FaPencil, FaTrash, FaTriangleExclamation, FaBolt } from 'react-icons/fa6';
import Badge from '@/components/Badges';
import { sourceMeta, describeDelimiter } from './constants';

/**
 * A store's input manifest: which files its feed is assembled from.
 *
 * Exactly one input is the trigger, so the control is a radio group rather than
 * per-row checkboxes. That mirrors the API — `PUT /sources/{id}/trigger` sets one
 * and clears the rest in a single transaction — and makes the "cannot clear the
 * only trigger" refusal unreachable by construction instead of an error to
 * surface. Shared inputs get a disabled radio: they are not part of this store's
 * upload, so their arrival is not this store's cycle.
 */
const DeclaredInputsCard = ({
  sources,
  storeId,
  sourcesSupported = true,
  busy,
  onAdd,
  onEdit,
  onDelete,
  onSetTrigger,
}) => {
  const triggerCount = sources.filter((s) => s.isTrigger).length;

  return (
    <div className="bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 rounded-lg">
      <div className="flex items-start justify-between gap-4 p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
              Declared inputs ({sources.length})
            </h3>
            {sources.length === 0 && (
              <Badge color="dark" size="xs">
                Single file
              </Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {sources.length === 0
              ? 'No manifest, so this store sends one file — registered as the table `source`. That is the normal case.'
              : 'Each row is registered as its own table, which the step SQL selects FROM. Exactly one input is the trigger: its arrival runs the chain.'}
          </p>
        </div>
        <button
          onClick={onAdd}
          disabled={!sourcesSupported || busy}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          <FaPlus className="w-3.5 h-3.5 mr-1.5" />
          Add input
        </button>
      </div>

      {!sourcesSupported && (
        <div className="m-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200">
          The <code className="font-mono">transform_sources</code> table is not present in this
          database, so every store reads as single-file. Apply{' '}
          <code className="font-mono">inventory-cdc/migrations/004_transform_sources.sql</code>.
        </div>
      )}

      {sources.length > 0 && triggerCount !== 1 && (
        <div className="mx-4 mt-4 rounded border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
          <div className="flex items-start gap-2">
            <FaTriangleExclamation className="w-4 h-4 mt-0.5 shrink-0 text-rose-500" />
            <p className="text-sm text-rose-800 dark:text-rose-200">
              {triggerCount === 0 ? (
                <>
                  <strong>No trigger.</strong> Every upload for this store is dropped without even
                  creating a job row, so nothing in the job list or history shows that it stopped
                  ingesting. Pick the file that lands last in an upload cycle.
                </>
              ) : (
                <>
                  <strong>{triggerCount} triggers.</strong> The cycle runs more than once and the
                  later run emits deltas correcting the first. Pick one.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {sources.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                <th className="px-4 py-2 font-medium">Trigger</th>
                <th className="px-4 py-2 font-medium">Table name</th>
                <th className="px-4 py-2 font-medium">File</th>
                <th className="px-4 py-2 font-medium">Delimiter</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {sources.map((source) => {
                const meta = sourceMeta(source.source);
                return (
                  <tr
                    key={source.id}
                    className="border-t border-slate-100 dark:border-slate-800 align-top"
                  >
                    <td className="px-4 py-3">
                      {source.shared ? (
                        <span
                          className="text-slate-300 dark:text-slate-600"
                          title="A shared input cannot be the trigger — it is not part of this store’s upload"
                        >
                          —
                        </span>
                      ) : (
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={`trigger-${storeId}`}
                            checked={source.isTrigger}
                            disabled={busy}
                            onChange={() => onSetTrigger(source)}
                            className="form-radio"
                          />
                          {source.isTrigger && (
                            <FaBolt
                              className="w-3 h-3 ml-1.5 text-amber-500"
                              title="Arrival of this file runs the chain"
                            />
                          )}
                        </label>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <span className="font-mono text-slate-800 dark:text-slate-100">
                        {source.name}
                      </span>
                    </td>

                    <td className="px-4 py-3 min-w-0">
                      <div className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all">
                        {source.fileName}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        {source.shared ? (
                          <Badge color="blue" size="xs" slim>
                            shared · full key
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-slate-400 dark:text-slate-500">
                            suffix
                          </span>
                        )}
                        {source.pattern && (
                          <Badge color="purple" size="xs" slim>
                            regexp
                          </Badge>
                        )}
                      </div>
                      {source.pattern && (
                        <div className="font-mono text-[11px] text-slate-500 dark:text-slate-400 mt-1 break-all">
                          {source.pattern}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {source.delimiter === null ? (
                        <span
                          className="text-xs text-slate-400 dark:text-slate-500"
                          title="Uses the store’s configured delimiter"
                        >
                          inherited
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-slate-700 dark:text-slate-200">
                          {describeDelimiter(source.delimiter)}
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <Badge color={meta.color} size="xs" slim>
                        {meta.label}
                      </Badge>
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => onEdit(source)}
                          disabled={busy}
                          title="Edit input"
                          className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 disabled:opacity-50"
                        >
                          <FaPencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onDelete(source)}
                          disabled={busy}
                          title="Delete input"
                          className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 disabled:opacity-50"
                        >
                          <FaTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sources.some((s) => s.warnings.length > 0) && (
        <ul className="px-4 pb-4 pt-2 space-y-1.5">
          {sources.flatMap((source) =>
            source.warnings.map((warning, i) => (
              <li
                key={`${source.id}-${i}`}
                className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300"
              >
                <FaTriangleExclamation className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <span className="font-mono">{source.name}</span>: {warning}
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default DeclaredInputsCard;
